import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { content, CONTACT_EMAIL } from "@/content/fitswag";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function SupportPage() {
  const { lang } = useLanguage();
  const t = content[lang].support;
  const faqItems = content[lang].faq.items;
  // Reuse existing FAQ: account deletion, data handling, nutritional accuracy, SWAG.
  const picks = [4, 3, 5, 1].map((i) => faqItems[i]).filter(Boolean);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16" data-testid="support-page">
        <section className="bg-black py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-5 md:px-8">
            <Link
              to="/"
              data-testid="support-back-home"
              className="mb-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/60 transition-colors hover:text-white"
            >
              <ArrowLeft size={16} /> Home
            </Link>

            <p className="overline text-brand-orange">{t.badge}</p>
            <h1
              data-testid="support-title"
              className="mt-3 font-display text-5xl leading-[0.95] tracking-tight text-white md:text-6xl"
            >
              {t.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-300">{t.intro}</p>

            <div className="mt-10 border border-white/10 bg-surface p-8">
              <p className="overline text-brand-orange">{t.contactLabel}</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="support-email"
                className="mt-3 flex items-center gap-3 font-display text-3xl tracking-tight text-white transition-colors hover:text-brand-orange md:text-4xl"
              >
                <Mail size={28} className="shrink-0" />
                {CONTACT_EMAIL}
              </a>

              <p className="overline mt-8 text-white/50">{t.topicsTitle}</p>
              <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {t.topics.map((topic, i) => (
                  <li
                    key={i}
                    data-testid={`support-topic-${i}`}
                    className="flex items-center gap-3 leading-relaxed text-gray-300"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 bg-brand-orange" />
                    {topic}
                  </li>
                ))}
              </ul>
            </div>

            <h2 className="mt-14 font-display text-3xl tracking-tight text-white md:text-4xl">
              {t.faqTitle}
            </h2>
            <Accordion type="single" collapsible className="mt-4 w-full" data-testid="support-faq">
              {picks.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`sfaq-${i}`}
                  data-testid={`support-faq-item-${i}`}
                  className="border-white/10"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-lg tracking-tight text-white hover:no-underline md:text-xl [&>svg]:text-brand-orange">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-gray-400">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-14 border-t border-white/10 pt-8">
              <p className="overline text-white/50">{t.legalTitle}</p>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                <Link
                  to="/privacy"
                  data-testid="support-privacy-link"
                  className="text-sm font-bold uppercase tracking-wider text-brand-orange hover:underline"
                >
                  {t.privacyLink}
                </Link>
                <Link
                  to="/terms"
                  data-testid="support-terms-link"
                  className="text-sm font-bold uppercase tracking-wider text-brand-orange hover:underline"
                >
                  {t.termsLink}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
